/**
 * OfficialHubRails · Feb 2026
 * =============================================================
 *
 * Composed 4-rail RGM Studio Official Hub presentation, reusable
 * across two hosts:
 *
 *   1) ``/rgm-studio`` (kept as an alias — redirects to canonical)
 *   2) ``/artist/{is_official.user_id}`` (canonical — Official Hub
 *      rails render inline based solely on ``is_official === true``,
 *      no dedicated tab)
 *
 * Reuses the already-built prop-driven rail components exported by
 * ``pages/OfficialProfilePage.js`` — no data duplication, no
 * component duplication, no backend duplication. The composite
 * ``GET /api/official/profile`` endpoint remains the single source
 * of truth; this wrapper simply orchestrates the layout.
 *
 * Data-flow:
 *   - If a parent already has the resolver payload (e.g.
 *     ``ArtistProfilePage`` fetches it once to decide whether to
 *     render the RGM Studio tab), it passes ``data`` and no
 *     additional network call is issued here.
 *   - If mounted standalone, this component performs its own
 *     ``GET /official/profile`` request.
 *
 * Runtime identity gate remains ``users.is_official == true`` on
 * the backend — this component never derives officialness from any
 * client-side hint.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Section,
  ReportCard,
  FeaturedArtistCard,
  SpotlightCard,
  ChampionCard,
  EmptyRail,
} from "@/pages/OfficialProfilePage";

const OfficialHubRails = ({ data: initialData = null }) => {
  const [state, setState] = useState(() => ({
    loading: !initialData,
    reports: initialData?.reports || [],
    featured_artists: initialData?.featured_artists || [],
    spotlight_winners: initialData?.spotlight_winners || [],
    contest_champions: initialData?.contest_champions || [],
  }));

  useEffect(() => {
    if (initialData) {
      setState({
        loading: false,
        reports: initialData.reports || [],
        featured_artists: initialData.featured_artists || [],
        spotlight_winners: initialData.spotlight_winners || [],
        contest_champions: initialData.contest_champions || [],
      });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/official/profile");
        if (cancelled) return;
        setState({
          loading: false,
          reports: r.data?.reports || [],
          featured_artists: r.data?.featured_artists || [],
          spotlight_winners: r.data?.spotlight_winners || [],
          contest_champions: r.data?.contest_champions || [],
        });
      } catch {
        if (cancelled) return;
        setState((s) => ({ ...s, loading: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialData]);

  if (state.loading) {
    return (
      <p
        className="text-gray-500 text-center py-16"
        data-testid="official-hub-rails-loading"
      >
        Loading RGM Studio hub…
      </p>
    );
  }

  return (
    <div data-testid="official-hub-rails">
      {/* 1. RGM Reports */}
      <Section
        title="RGM Reports"
        emoji="📰"
        subtitle="News, winners, and platform announcements — always live."
        viewAllTo="/reports"
        testId="official-section-reports"
      >
        {state.reports.length === 0 ? (
          <EmptyRail label="No published reports yet." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {state.reports.map((r) => (
              <ReportCard key={r.report_id} report={r} />
            ))}
          </div>
        )}
      </Section>

      {/* 2. Featured Artists */}
      <Section
        title="Featured Artists"
        emoji="⭐"
        subtitle="Highlighted artists across RGM Studio right now."
        viewAllTo="/explore"
        testId="official-section-featured"
      >
        {state.featured_artists.length === 0 ? (
          <EmptyRail label="No featured artists to show yet." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {state.featured_artists.map((artist, i) => (
              <FeaturedArtistCard
                key={artist.user_id}
                artist={artist}
                rank={i + 1}
              />
            ))}
          </div>
        )}
      </Section>

      {/* 3. Spotlight Winners */}
      <Section
        title="Spotlight Winners"
        emoji="🌟"
        subtitle="Recent published Spotlight weeks — canonical winners."
        viewAllTo="/spotlight"
        testId="official-section-spotlight"
      >
        {state.spotlight_winners.length === 0 ? (
          <EmptyRail label="No Spotlight winners published yet." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {state.spotlight_winners.map((row) => (
              <SpotlightCard
                key={`${row.spotlight_id}-${row.submission_id}`}
                row={row}
              />
            ))}
          </div>
        )}
      </Section>

      {/* 4. Contest Champions */}
      <Section
        title="Contest Champions"
        emoji="🏆"
        subtitle="Recent Hall of Champions honors."
        viewAllTo="/winners"
        testId="official-section-champions"
      >
        {state.contest_champions.length === 0 ? (
          <EmptyRail label="No contest champions to show yet." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {state.contest_champions.map((row) => (
              <ChampionCard
                key={row.champion_id || row.entry_ref_id}
                row={row}
              />
            ))}
          </div>
        )}
      </Section>
    </div>
  );
};

export default OfficialHubRails;
